import React, { useState, useEffect, useRef } from 'react';
import { PillContext, usePill } from './searchresultpillcontext';
import { usePillActions } from './usepillactions';

/**
 * React component that renders a pill button which can:
 *   - Drop down a list of links to external sites or open a modal (http, ftp, api+s3 types)
 *   - Act as a plain button that opens an API modal (api-only type)
 *
 * @param {string}  props.pillLabel  - Label displayed on the pill.
 * @param {string}  props.type       - Pill type: "http" | "api" | "ftp" | "helioclouds3"
 * @param {boolean} props.hasS3      - Whether the dataset also has an S3 access method.
 * @param {string}  props.hcutId     - Dataset ID used to fetch backend data.
 */
export function SearchResultDropdownPill({ pillLabel, type, hasS3, hcutId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState([]);
  const [apiData, setApiData] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [isFtpModalOpen, setFtpModalOpen] = useState({});
  const [isApiModalOpen, setApiModalOpen] = useState({ api: false });
  const [isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen] = useState({});
  const [currentModalId, setCurrentModalId] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const contextValue = {
    hcutId,
    type,
    hasS3,
    data, setData,
    apiData, setApiData,
    modalData, setModalData,
    isOpen, setIsOpen,
    isFtpModalOpen, setFtpModalOpen,
    isApiModalOpen, setApiModalOpen,
    isHeliocloudS3ModalOpen, setHeliocloudS3ModalOpen,
    currentModalId, setCurrentModalId,
  };

  return (
    <PillContext.Provider value={contextValue}>
      <PillInner pillLabel={pillLabel} dropdownRef={dropdownRef} />
    </PillContext.Provider>
  );
}


/**
 * Inner render component. Separated so it can consume PillContext via hooks.
 *
 * @param {string} props.pillLabel   - Label text for the pill.
 * @param {object} props.dropdownRef - Ref attached to the dropdown container for outside-click detection.
 */
function PillInner({ pillLabel, dropdownRef }) {
  const {
    type,
    hasS3,
    data,
    isOpen,
    isFtpModalOpen,
    isApiModalOpen,
    isHeliocloudS3ModalOpen,
    currentModalId,
    modalData,
  } = usePill();

  const {
    handleToggleDropdown,
    handleToggleApiModal,
  } = usePillActions();

  const clNmMap = {
    http: 'searchResultsPillBlue',
    api: 'searchResultsPillGreen',
    ftp: 'searchResultsPillBrown',
  };

  // API-only pill (no S3): no dropdown arrow, clicking opens API modal directly
  const isApiOnly = type === 'api' && !hasS3;
  const suffix = isApiOnly ? '' : '▼';
  const clNm = isApiOnly ? clNmMap[type] : `${clNmMap[type]}LeftHalf`;

  const onPillClick = isApiOnly
    ? () => handleToggleApiModal()
    : () => handleToggleDropdown(hasS3);

  return (
    <div className="">
      <div
        className="dropdownPillContainer dropdownPillObject"
        ref={dropdownRef}>

        {/* Left label half — sole trigger for open/close, no duplicate handlers */}
        <span className={clNm} onClick={onPillClick}>
          {pillLabel}
        </span>

        {/* Right arrow half — only shown when there's a dropdown */}
        {!isApiOnly && (
          <span
            className={clNm.replace('Left', 'Right')}
            onClick={onPillClick}>
            {suffix}
          </span>
        )}

        {/* HTTP dropdown — stopPropagation so clicks inside don't retrigger onPillClick */}
        {isOpen && type === 'http' && (
          <div
            className={`dropdownContent dropdownContentOpen`}
            onClick={e => e.stopPropagation()}>
            <ul className="dropdownList">
              {data.httpRows?.map((row, i) => <li key={i}>{row}</li>)}
            </ul>
          </div>
        )}

        {/* FTP dropdown */}
        {isOpen && type === 'ftp' && (
          <div
            className={`dropdownContent dropdownContentOpen`}
            onClick={e => e.stopPropagation()}>
            {data.ftpRows?.map((row, i) => <div key={i}>{row}</div>)}
          </div>
        )}

        {/* API + S3 dropdown */}
        {isOpen && type === 'api' && hasS3 && (
          <div
            className={`dropdownContent dropdownContentOpen`}
            onClick={e => e.stopPropagation()}>
            {data.heliocloudS3Rows?.map((row, i) => <div key={i}>{row}</div>)}
          </div>
        )}
      </div>

      {/* Modal rendering */}
      <div id="modal">
        {((type === 'ftp'  && isFtpModalOpen[currentModalId]) ||
          (type === 'api'  && isApiModalOpen['api']) ||
          (hasS3           && isHeliocloudS3ModalOpen[currentModalId]) ||
          (hasS3           && isApiModalOpen['api'])
        ) && modalData}
      </div>
    </div>
  );
}
